import { ChangeDetectorRef, Component, HostListener, NgZone, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core'
import { SquidexCacheService } from '../../../../services/squidex-cache.service'
import { SquidexService } from '../../../../services/squidex.service'
import { isScullyGenerated, TransferStateService } from '@scullyio/ng-lib'
import { Observable, of, Subject, interval, tap, takeUntil } from 'rxjs'

@Component({
  selector: 'flowaccount-dac-sticky-banner',
  templateUrl: './dac-sticky-banner.component.html',
  styleUrls: ['./dac-sticky-banner.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DacStickyBannerComponent implements OnInit, OnDestroy {
  public serviceList$: Observable<object[]>
  public showFloatingBar = false
  public countdown: { days: number; hours: number; minutes: number; seconds: number } | null = null

  private readonly _cacheKey = 'DAC_STICKY_KEY'
  private readonly _squidexContentKey = 'dac-sticky-banner'
  private destroy$ = new Subject<void>()
  private introBottom = 0
  private promoTop = 0

  constructor(
    private squidexService: SquidexService,
    private squidexCacheService: SquidexCacheService,
    private transferState: TransferStateService,
    private cdref: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {}

  @HostListener('window:scroll', [])
  onWindowScroll(): void {
    const intro = document.getElementById('dacIntro-id')
    const promo = document.getElementById('dacPastEven-id')

    if (!intro || !promo) return

    const scrollTop = window.scrollY
    this.introBottom = intro.offsetTop + intro.offsetHeight
    this.promoTop = promo.getBoundingClientRect().top + scrollTop

    const isBelowIntro = scrollTop > this.introBottom
    const isAbovePromo = scrollTop < this.promoTop - 100

    this.showFloatingBar = isBelowIntro && isAbovePromo
  }

  ngOnInit(): void {
    if (isScullyGenerated()) {
      this.serviceList$ = this.transferState.useScullyTransferState<object[]>(this._cacheKey, of([]))
    } else {
      this.serviceList$ = this.squidexCacheService
        .getServiceList$(
          this._cacheKey,
          this._squidexContentKey,
          this.squidexService.getContentSquidexApi.bind(this.squidexService),
        )
        .pipe(
          tap((data: any[]) => {
            this.transferState.setState(this._cacheKey, data)
            const countdownDateStr = data[0]?.data?.countdownDate?.iv
            if (countdownDateStr) {
              const target = new Date(countdownDateStr)
              target.setHours(0, 0, 0, 0)

              const now = new Date()
              if (now < target) {
                this.startCountdown(target)
              } else {
                this.showFloatingBar = false
              }
            }
          }),
        )
    }
  }

  startCountdown(target: Date): void {
    this.ngZone.runOutsideAngular(() => {
      interval(1000)
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          const now = Date.now()
          const distance = target.getTime() - now

          if (distance <= 0) {
            this.ngZone.run(() => {
              this.countdown = null
              this.showFloatingBar = false
              this.destroy$.next()
              this.cdref.detectChanges()
            })
            return
          }

          const next = {
            days: Math.floor(distance / (1000 * 60 * 60 * 24)),
            hours: Math.floor((distance / (1000 * 60 * 60)) % 24),
            minutes: Math.floor((distance / (1000 * 60)) % 60),
            seconds: Math.floor((distance / 1000) % 60),
          }

          const isDifferent =
            !this.countdown ||
            next.days !== this.countdown.days ||
            next.hours !== this.countdown.hours ||
            next.minutes !== this.countdown.minutes ||
            next.seconds !== this.countdown.seconds

          if (isDifferent) {
            this.ngZone.run(() => {
              this.countdown = next
              this.cdref.detectChanges()
            })
          }
        })
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }
}
